import { Fragment, useEffect, useRef, useState, type ReactNode } from "react";
import { observer } from "mobx-react-lite";
import { appState } from "@/app/appState";
import { documentFileUrl } from "@/apiExtra/documentFiles";
// Must precede the pdfjs import; see mapUpsertPolyfill.
import "@/components/mapUpsertPolyfill";
import * as pdfjsLib from "pdfjs-dist";
import pdfWorker from "@/components/pdfWorkerEntry?worker&url";
import { containNestedScroll } from "@/components/scrollUtils";
import { blocksFromPdfTextContent, pdfBlocksCharCount, type PdfBlock, type PdfInlineRun, type PdfJsTextContent } from "@/components/pdfTextLayout";

pdfjsLib.GlobalWorkerOptions.workerSrc = pdfWorker;

type Props = {
    documentId: number;
    filename?: string | null;
    fill?: boolean;
};

type PdfPageView =
    | { type: "text"; blocks: PdfBlock[] }
    | { type: "bitmap" };

const Inline = ({ runs }: { runs: PdfInlineRun[] }) => (
    <>
        {runs.map((run, i) => {
            let node: ReactNode = run.text;
            if (run.sub) node = <sub>{run.text}</sub>;
            else if (run.sup) node = <sup>{run.text}</sup>;
            if (run.italic) node = <em>{node}</em>;
            if (run.bold) node = <strong>{node}</strong>;
            return <Fragment key={i}>{node}</Fragment>;
        })}
    </>
);

const PdfBlockView = ({ block }: { block: PdfBlock }) => {
    switch (block.kind) {
        case "h1":
            return <h1><Inline runs={block.runs} /></h1>;
        case "h2":
            return <h2><Inline runs={block.runs} /></h2>;
        case "byline":
            return <p className="pdf-byline"><Inline runs={block.runs} /></p>;
        default:
            return <p><Inline runs={block.runs} /></p>;
    }
};

export const PdfViewer = observer(({ documentId, filename, fill }: Props) => {
    const scrollRef = useRef<HTMLDivElement>(null);
    const bitmapHostRef = useRef<HTMLDivElement>(null);
    const pdfRef = useRef<pdfjsLib.PDFDocumentProxy | null>(null);
    const loadingTaskRef = useRef<ReturnType<typeof pdfjsLib.getDocument> | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [loading, setLoading] = useState(true);
    const [pages, setPages] = useState<PdfPageView[]>([]);

    useEffect(() => {
        const el = scrollRef.current;
        if (!el) return;
        return containNestedScroll(el);
    }, [documentId, fill]);

    useEffect(() => {
        let cancelled = false;
        pdfRef.current = null;
        loadingTaskRef.current = null;
        setLoading(true);
        setError(null);
        setPages([]);

        (async () => {
            const res = await fetch(documentFileUrl(documentId), { credentials: "include" });
            if (!res.ok) throw new Error(appState.t("chat.failedToLoad"));
            const data = await res.arrayBuffer();
            const loadingTask = pdfjsLib.getDocument({ data });
            loadingTaskRef.current = loadingTask;
            const pdf = await loadingTask.promise;
            if (cancelled) {
                loadingTask.destroy();
                return;
            }
            pdfRef.current = pdf;

            const nextPages: PdfPageView[] = [];
            for (let i = 1; i <= pdf.numPages; i++) {
                const page = await pdf.getPage(i);
                if (cancelled) return;
                const textContent = await page.getTextContent() as PdfJsTextContent;
                const blocks = blocksFromPdfTextContent(textContent);
                nextPages.push(pdfBlocksCharCount(blocks) >= 40 ? { type: "text", blocks } : { type: "bitmap" });
            }

            if (cancelled) return;
            setPages(nextPages);
            setLoading(false);
        })().catch((e) => {
            if (cancelled) return;
            setError(e instanceof Error ? e.message : appState.t("chat.failedToLoad"));
            setLoading(false);
        });

        return () => {
            cancelled = true;
            loadingTaskRef.current?.destroy();
            loadingTaskRef.current = null;
            pdfRef.current = null;
        };
    }, [documentId]);

    useEffect(() => {
        const pdf = pdfRef.current;
        const host = bitmapHostRef.current;
        if (!pdf || !host || !pages.some((p) => p.type === "bitmap")) return;

        let cancelled = false;
        const canvases: HTMLCanvasElement[] = [];

        (async () => {
            const placeholders = host.querySelectorAll<HTMLElement>("[data-pdf-bitmap]");
            let bitmapIndex = 0;
            const dpr = Math.min(window.devicePixelRatio || 1, 2);
            for (let i = 1; i <= pdf.numPages; i++) {
                if (pages[i - 1]?.type !== "bitmap") continue;
                const mount = placeholders[bitmapIndex++];
                if (!mount) continue;
                const page = await pdf.getPage(i);
                if (cancelled) return;
                const unscaled = page.getViewport({ scale: 1 });
                const cssWidth = mount.clientWidth || host.clientWidth || 640;
                const scale = (cssWidth / unscaled.width) * dpr;
                const viewport = page.getViewport({ scale });
                const canvas = document.createElement("canvas");
                canvas.width = viewport.width;
                canvas.height = viewport.height;
                canvas.className = "w-full h-auto";
                const ctx = canvas.getContext("2d");
                if (!ctx) continue;
                await page.render({ canvasContext: ctx, viewport, canvas }).promise;
                if (cancelled) return;
                mount.replaceChildren(canvas);
                canvases.push(canvas);
            }
        })();

        return () => {
            cancelled = true;
            canvases.forEach((c) => c.remove());
        };
    }, [pages]);

    return (
        <div
            ref={scrollRef}
            className={`doc-viewer ${fill ? "doc-viewer-fill" : ""}`}
            title={filename || appState.t("objects.pdf")}
            data-pages={pages.length || undefined}
        >
            {loading && (
                <div className="doc-status">{appState.t("common.loading")}</div>
            )}
            {error && (
                <div className="doc-status">{error}</div>
            )}
            <div ref={bitmapHostRef} className="pdf-artifact px-1 py-1">
                {pages.map((page, i) => (
                    page.type === "text" ? (
                        <section key={i} className="pdf-page">
                            {page.blocks.map((block, j) => (
                                <PdfBlockView key={j} block={block} />
                            ))}
                        </section>
                    ) : (
                        <div
                            key={i}
                            data-pdf-bitmap
                            className={i > 0 ? "mt-2" : undefined}
                        />
                    )
                ))}
            </div>
        </div>
    );
});
